/**
 * Statistical power and sample size, the way G*Power and R's `pwr` compute them.
 *
 * - t-tests (independent two-sample with equal groups, and paired / one-sample) use the
 *   exact noncentral t: power = P(T′ > t_crit) (+ P(T′ < −t_crit) two-tailed), with
 *   δ = d √(n/2) on 2n − 2 df, or δ = d √n on n − 1 df. This is what makes d = 0.5,
 *   α = .05 two-tailed, power .80 come out at 64 per group, as in G*Power.
 * - Two independent proportions use Cohen's h = 2 arcsin √p₁ − 2 arcsin √p₂ and the
 *   normal approximation of `pwr.2p.test`: power = Φ(h √(n/2) − z_crit) plus the
 *   opposite tail when two-tailed.
 *
 * Effect sizes are used as magnitudes; a one-tailed test assumes the effect lies in the
 * direction the alternative hypothesis predicts.
 */

import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  normalCdf,
  normalQuantile,
  normalUpper,
  studentTQuantile,
} from "@/lib/stats/distributions";
import { noncentralTCdf } from "@/lib/stats/noncentral-t";

export type PowerDesign = "independent" | "paired" | "proportions";
export type Tails = 1 | 2;

export interface PowerPoint {
  power: number;
  /** The critical value the statistic must pass: t (t-tests) or z (proportions). */
  critical: number;
  /** Noncentrality δ for t-tests; h √(n/2) for proportions. */
  noncentrality: number;
  /** Degrees of freedom (t-tests only). */
  df?: number;
}

/** The smallest n each design accepts: one per group would leave no df. */
export const MIN_N = 2;
/** Sample sizes beyond this are reported as impractical rather than searched for. */
export const MAX_N = 10_000_000;

export function cohensH(p1: number, p2: number): number {
  return 2 * Math.asin(Math.sqrt(p1)) - 2 * Math.asin(Math.sqrt(p2));
}

export function tTestPower(
  d: number,
  n: number,
  alpha: number,
  tails: Tails,
  design: "independent" | "paired"
): PowerPoint {
  const effect = Math.abs(d);
  const df = design === "independent" ? 2 * n - 2 : n - 1;
  const ncp =
    design === "independent"
      ? effect * Math.sqrt(n / 2)
      : effect * Math.sqrt(n);
  const critical = studentTQuantile(1 - alpha / tails, df);
  const upper = 1 - noncentralTCdf(critical, df, ncp);
  const power =
    tails === 2 ? upper + noncentralTCdf(-critical, df, ncp) : upper;
  return {
    power: Math.min(1, Math.max(0, power)),
    critical,
    noncentrality: ncp,
    df,
  };
}

export function proportionsPower(
  h: number,
  n: number,
  alpha: number,
  tails: Tails
): PowerPoint {
  const shift = Math.abs(h) * Math.sqrt(n / 2);
  const critical = normalQuantile(1 - alpha / tails);
  const upper = normalUpper(critical - shift);
  const power = tails === 2 ? upper + normalCdf(-critical - shift) : upper;
  return { power: Math.min(1, power), critical, noncentrality: shift };
}

export interface PowerProblem {
  design: PowerDesign;
  /** Cohen's d for the t-tests, Cohen's h for proportions. */
  effect: number;
  alpha: number;
  tails: Tails;
}

export function powerAt(problem: PowerProblem, n: number): PowerPoint {
  if (problem.design === "proportions") {
    return proportionsPower(problem.effect, n, problem.alpha, problem.tails);
  }
  return tTestPower(
    problem.effect,
    n,
    problem.alpha,
    problem.tails,
    problem.design
  );
}

export function isValidProblem(problem: PowerProblem): boolean {
  return (
    Number.isFinite(problem.effect) &&
    problem.effect !== 0 &&
    problem.alpha > 0 &&
    problem.alpha < 1
  );
}

/**
 * The smallest n (per group, or pairs) whose power reaches `target`, or null when even
 * `MAX_N` falls short. Power rises with n, so an exponential bracket and a binary search
 * find it in a few dozen evaluations however small the effect.
 */
export function requiredSampleSize(
  problem: PowerProblem,
  target: number
): { n: number; point: PowerPoint } | null {
  if (!(isValidProblem(problem) && target > 0 && target < 1)) {
    return null;
  }
  const reaches = (n: number) => powerAt(problem, n).power >= target;
  if (reaches(MIN_N)) {
    return { n: MIN_N, point: powerAt(problem, MIN_N) };
  }
  let low = MIN_N;
  let high = MIN_N * 2;
  while (!reaches(high)) {
    if (high >= MAX_N) {
      return null;
    }
    low = high;
    high = Math.min(MAX_N, high * 2);
  }
  while (high - low > 1) {
    const mid = Math.floor((low + high) / 2);
    if (reaches(mid)) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return { n: high, point: powerAt(problem, high) };
}

/** About `count` points of the power curve from MIN_N to `nMax`. */
export function powerCurve(
  problem: PowerProblem,
  nMax: number,
  count = 60
): { n: number; power: number }[] {
  const top = Math.max(MIN_N + 1, Math.ceil(nMax));
  const step = Math.max(1, Math.ceil((top - MIN_N) / count));
  const points: { n: number; power: number }[] = [];
  for (let n = MIN_N; n < top; n += step) {
    points.push({ n, power: powerAt(problem, n).power });
  }
  points.push({ n: top, power: powerAt(problem, top).power });
  return points;
}

/** The right end of the curve: twice the n of interest, and never a tiny window. */
export function curveLimit(n: number): number {
  return Math.max(20, Math.ceil(n * 2));
}

/* ------------------------------------------------------------- the form */

/** The calculator's inputs as typed: every number still a string. */
export interface PowerForm {
  design: PowerDesign;
  solve: "n" | "power";
  /** Cohen's d, for the t-tests. */
  d: string;
  /** The two expected proportions, for the proportions test. */
  p1: string;
  p2: string;
  alpha: string;
  tails: Tails;
  /** Target power, when solving for n. */
  target: string;
  /** n per group (or pairs), when solving for power. */
  n: string;
}

export type PowerFormProblem =
  | "effect"
  | "proportions"
  | "alpha"
  | "target"
  | "n";

export interface SolvedPower {
  kind: "ok";
  problem: PowerProblem;
  n: number;
  point: PowerPoint;
}

export type PowerOutcome =
  | { kind: "problem"; problem: PowerFormProblem }
  | { kind: "tooLarge" }
  | SolvedPower;

/** The effect size for the chosen design — d, or h from p₁ and p₂ — or what is wrong. */
function readEffect(form: PowerForm): number | PowerFormProblem {
  if (form.design !== "proportions") {
    const d = parseNumberInput(form.d);
    return d === null || d === 0 ? "effect" : d;
  }
  const p1 = parseNumberInput(form.p1);
  const p2 = parseNumberInput(form.p2);
  const valid = (p: number | null): p is number =>
    p !== null && p >= 0 && p <= 1;
  if (!(valid(p1) && valid(p2)) || p1 === p2) {
    return "proportions";
  }
  return cohensH(p1, p2);
}

/** Parse the form and solve it for whichever unknown it asks about. */
export function solvePower(form: PowerForm): PowerOutcome {
  const effect = readEffect(form);
  if (typeof effect === "string") {
    return { kind: "problem", problem: effect };
  }
  const alpha = parseNumberInput(form.alpha);
  if (alpha === null || alpha <= 0 || alpha >= 1) {
    return { kind: "problem", problem: "alpha" };
  }
  const problem: PowerProblem = {
    design: form.design,
    effect,
    alpha,
    tails: form.tails,
  };
  if (form.solve === "power") {
    const n = parseIntegerInput(form.n);
    if (n === null || n < MIN_N) {
      return { kind: "problem", problem: "n" };
    }
    return { kind: "ok", problem, n, point: powerAt(problem, n) };
  }
  const target = parseNumberInput(form.target);
  if (target === null || target <= alpha || target >= 1) {
    return { kind: "problem", problem: "target" };
  }
  const found = requiredSampleSize(problem, target);
  if (!found) {
    return { kind: "tooLarge" };
  }
  return { kind: "ok", problem, n: found.n, point: found.point };
}
